import { Component, Element, h, Host, Listen, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import {
  BusinesscardContactSchemeDefinition,
  BusinesscardContactSchemeName,
  InfoTextSize,
  LinkLocalization
} from "./constants/businesscard-contact-types";
import { shortenLink, stringFormat } from "../../utils/string-format";
import {
  BUSINESSCARD_CONTACT_SCHEME_CONFIG,
  BUSINESSCARD_CONTACT_SCHEME_MAP,
  CARD_INFO_TEXT_SIZE_ABSTRACT,
  CARD_INFO_TEXT_SIZE_H5,
  LinkIcons
} from "./constants/businesscard-contact-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { isMobile } from "../../utils/screen-helper";
import { getFirstNestedChild, getNestedChildrenOf } from "../../utils/dom-helper";
import { LINK_LOCALIZATION } from "./constants/businesscard-contact-i18n";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForHeadline, useTheRightHtmlTagForSubheadline } from "../../utils/headline-helper";
import { BusinesscardContactSchemeConfiguration } from "../../constants/colors";
import {
  BUSINESSCARD_CONTACT_DEFAULT_ALIGNMENT,
  BUSINESSCARD_CONTACT_HEADLINE_SEO_TAG,
  BUSINESSCARD_CONTACT_HEADLINE_SIZE,
  BusinesscardContactAlignment,
  LINK_ICON_POSITION_LEFT
} from "../../constants/global-constants";
import {
  ContainerBreakpointComponent,
  initContainerBreakpointsDelegate
} from "../../common/container-breakpoint-component";
import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot image - You can put an <eon-ui-image> component in that slot for businesscard image.
 * @slot links - You can put an <eon-ui-link> component in that slot for businesscard links.
 */
@Component({
  tag: "eon-ui-businesscard-contact",
  styleUrl: "businesscard-contact.scss",
  shadow: true
})
export class BusinesscardContact
  implements
    ColorableComponent<BusinesscardContactSchemeConfiguration<BusinesscardContactSchemeDefinition>>,
    LocalizableComponent<LinkLocalization>,
    ContainerBreakpointComponent {
  @InitLogging() sentryTransaction;
  schemeConfig: BusinesscardContactSchemeConfiguration<BusinesscardContactSchemeDefinition> = BUSINESSCARD_CONTACT_SCHEME_CONFIG;
  schemeMap = BUSINESSCARD_CONTACT_SCHEME_MAP;

  /**
   * Array that defines component specific container breakpoints.
   */
  containerBreakpoints = [1200, 800, 400];

  @State() activeScheme: BusinesscardContactSchemeDefinition;
  @State() seoHeadlineTag: string;
  @State() seoSubheadlineTag: string;

  localization: Localization<LinkLocalization> = LINK_LOCALIZATION;
  currentLocalization: LinkLocalization;

  private mailAreaLabel: string;
  private phoneAreaLabel: string;
  private faxAreaLabel: string;

  @Element() hostElement: HTMLEonUiBusinesscardContactElement;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * The color scheme of the icon.
   */
  @Prop({ reflect: true }) scheme: BusinesscardContactSchemeName;

  /**
   * Name text of the component.
   */
  @Prop() name: string;

  /**
   * Description text of the component.
   */
  @Prop() info: string;

  /**
   * Alignment of the card component
   */
  @Prop() align: BusinesscardContactAlignment = BUSINESSCARD_CONTACT_DEFAULT_ALIGNMENT;

  /**
   * mail addresss used in the card component.
   */
  @Prop() mail: string;

  /**
   * Text for Mail link, if no text is provided, the mail address is used
   */
  @Prop() mailText: string;

  /**
   * phone address used in the card component.
   */
  @Prop() phone: string;

  /**
   * fax address used in the card component.
   */
  @Prop() fax: string;

  /**
   * fax text used in the card component., if none is set the fax number is used
   */
  @Prop() faxText: string;

  /**
   * phone text used in the card component., if none is set the phone number is used
   */
  @Prop() phoneText: string;

  /**
   * facebook icon used in the card component.
   */
  @Prop() facebook: string;

  /**The account name of facebook */
  @Prop() facebookAccountName: string;

  /**
   * linkedin icon used in the card component.
   */
  @Prop() linkedin: string;

  /**
   * the account name of linkedin
   */
  @Prop() linkedinAccountName: string;

  /**
   * twitter icon used in the card component.
   */
  @Prop() twitter: string;

  /**
   * the account name twitter
   */
  @Prop() twitterAccountName: string;

  /**
   * instagram icon used in the card component.
   */
  @Prop() instagram: string;

  /**
   * the account name instagram
   */
  @Prop() instagramAccountName: string;

  /**Secondary Address of the contact. The "," are getting converted to a linebreak */
  @Prop() primaryAddress: string;

  /**Primary Address of the contact. The "," are getting converted to a linebreak */
  @Prop() secondaryAddress: string;

  /**The actually link address */
  private googleMapsAddress(address: string) {
    return `https://maps.google.com/?q=${address}`;
  }

  /**
   * changes how the businesscard is aligned
   */
  @Prop() vertical: boolean = true;

  /**
   * Control if the image should have an offset on large desktops
   */
  @Prop() imageOffset: boolean = false;

  /**
   * the option to select abstract text-style
   */
  @Prop() infoTextSize: InfoTextSize = CARD_INFO_TEXT_SIZE_H5;

  /**
   * Internal property to switch between vertical and horizontal
   */
  @State() verticalIntern: boolean;

  /**
   * Internal property to switch offset on and off
   */
  @State() offsetIntern: boolean;

  /**
   * hasImage verifies whether image is present or not and depending on image availability
   * businesscard background will occupy the remaining space
   */
  @State() hasImage: boolean;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for the name- and info-properties,<br>
   *   the HTML-Tag for the info (subheadline) is set automatically depending on this property (headline-html-tag  -1 ), e.g <br>
   *    name(Headline) is H2, then info(subheadline) is H3, name(Headline) is H4, then info(subheadline) is H5, and so on
   *
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  @Listen("resize", { target: "window" })
  onViewportSizeChange() {
    this.setVerticalIntern(this.vertical);
  }

  initContainerBreakpoints(): void {
    initContainerBreakpointsDelegate(this.hostElement, this.containerBreakpoints);
  }

  componentWillLoad() {
    this.applyScheme();
    this.initContainerBreakpoints();
    this.setCurrentLocalization();
    this.computeAriaLabels();
    this.setVerticalIntern(this.vertical);
    this.offsetIntern = this.imageOffset;
    this.handleHeadlineTag();
    this.onViewportSizeChange();
    this.setOffsetIntern();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.setImageAspectRatio();
    this.setLinkIconCenter();
  }

  @Watch("vertical")
  setVerticalIntern(vertical: boolean): void {
    const isMobileDevice = isMobile();

    if (isMobileDevice) {
      this.verticalIntern = true;
    } else {
      this.verticalIntern = vertical;
    }
  }

  @Watch("imageOffset")
  @Watch("verticalIntern")
  setOffsetIntern(): void {
    if (!this.verticalIntern) {
      //always show image offset on horizontal cards
      this.offsetIntern = true;
    } else {
      this.offsetIntern = this.imageOffset;
    }
  }

  @Watch("verticalIntern")
  setImageAspectRatio(): void {
    const image: HTMLEonUiLinkElement = getFirstNestedChild(this.hostElement, "eon-ui-image");
    if (image) {
      this.hasImage = true;
      if (this.verticalIntern) {
        image.setAttribute("aspect-ratio", "ratio-4x3");
      } else {
        if (isMobile()) {
          image.setAttribute("aspect-ratio", "ratio-4x3");
        } else {
          image.setAttribute("aspect-ratio", "ratio-1x1");
        }
      }
    } else {
      this.hasImage = false;
    }
  }

  private setLinkIconCenter: () => void = () => {
    let centerLink = Array.from(getNestedChildrenOf(this.hostElement, "eon-ui-link"));
    centerLink?.forEach((slotLink) => {
      if (slotLink?.slot === "links") {
        slotLink?.setAttribute("align-icon-center", "true");
        slotLink?.setAttribute("icon-position", LINK_ICON_POSITION_LEFT);
      }
    });
  };

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("mail")
  @Watch("phone")
  private computeAriaLabels() {
    if (this.phone) {
      this.phoneAreaLabel = stringFormat(this.currentLocalization.ariaLabel.phone, this.phone);
    }
    if (this.mail) {
      this.mailAreaLabel = stringFormat(this.currentLocalization.ariaLabel.mail, this.mail);
    }
    if (this.fax) {
      this.faxAreaLabel = stringFormat(this.currentLocalization.ariaLabel.fax, this.fax);
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, BUSINESSCARD_CONTACT_HEADLINE_SEO_TAG);
    this.seoSubheadlineTag = useTheRightHtmlTagForSubheadline(
      this.headlineHtmlTag,
      BUSINESSCARD_CONTACT_HEADLINE_SEO_TAG
    );
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            businesscard: true,
            [`businesscard--${this.align}`]: true,
            "businesscard--vertical": this.verticalIntern,
            "businesscard--horizontal": !this.verticalIntern,
            "businesscard--no-image": !this.hasImage,
            [`businesscard${this.offsetIntern ? "--offset" : "--no-offset"}`]: true
          })}
        >
          {this.hasImage && (
            <div class={pfx({ businesscard__image: true, "businesscard__image--offset": this.offsetIntern })}>
              <slot name="image"></slot>
            </div>
          )}
          <div
            class={pfx({
              "card-content": true
            })}
          >
            <div class={pfx({ businesscard__content: true, "businesscard__content--offset": this.offsetIntern })}>
              <div class={pfx("businesscard__headlines")}>
                <eon-ui-headline
                  size={BUSINESSCARD_CONTACT_HEADLINE_SIZE}
                  alignment={this.align}
                  headline-html-tag={this.seoHeadlineTag}
                  class={pfx({ businesscard__name: true })}
                  text={this.name}
                ></eon-ui-headline>
                {this.infoTextSize === CARD_INFO_TEXT_SIZE_H5 && (
                  <eon-ui-headline
                    size={CARD_INFO_TEXT_SIZE_H5}
                    alignment={this.align}
                    headline-html-tag={this.seoSubheadlineTag}
                    class={pfx("businesscard__info")}
                    text={this.info}
                  ></eon-ui-headline>
                )}
                {this.infoTextSize === CARD_INFO_TEXT_SIZE_ABSTRACT && (
                  <eon-ui-text text-style="abstract" class={pfx("businesscard__info")}>
                    <span innerHTML={this.info}></span>
                  </eon-ui-text>
                )}
              </div>
              <div class={pfx("businesscard__links")}>
                {this.primaryAddress && (
                  <eon-ui-link
                    target="_blank"
                    href={this.googleMapsAddress(this.primaryAddress)}
                    align-icon-center="true"
                    text={this.primaryAddress.replace(new RegExp(",", "g"), "<br>")}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.secondaryAddress && (
                  <eon-ui-link
                    target="_blank"
                    href={this.googleMapsAddress(this.secondaryAddress)}
                    align-icon-center="true"
                    text={this.secondaryAddress.replace(new RegExp(",", "g"), "<br>")}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.mail && (
                  <eon-ui-link
                    target="_blank"
                    ariaLabel={this.mailAreaLabel}
                    icon={LinkIcons.mail}
                    href={"mailto:" + this.mail}
                    align-icon-center="true"
                    text={this.mailText ? this.mailText : this.mail}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.phone && (
                  <eon-ui-link
                    target="_self"
                    ariaLabel={this.phoneAreaLabel}
                    icon={LinkIcons.phone}
                    href={"tel:" + this.phone}
                    align-icon-center="true"
                    text={this.phoneText ? this.phoneText : this.phone}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.fax && (
                  <eon-ui-link
                    target="_self"
                    ariaLabel={this.faxAreaLabel}
                    icon={LinkIcons.fax}
                    href={"fax:" + this.fax}
                    align-icon-center="true"
                    text={this.faxText ? this.faxText : this.fax}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.facebook && (
                  <eon-ui-link
                    target="_blank"
                    icon={LinkIcons.facebook}
                    href={this.facebook}
                    align-icon-center="true"
                    text={this.facebookAccountName ? this.facebookAccountName : shortenLink(this.facebook)}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.linkedin && (
                  <eon-ui-link
                    target="_blank"
                    icon={LinkIcons.linkedin}
                    href={this.linkedin}
                    align-icon-center="true"
                    text={this.linkedinAccountName ? this.linkedinAccountName : shortenLink(this.linkedin)}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.twitter && (
                  <eon-ui-link
                    target="_blank"
                    icon={LinkIcons.twitter}
                    href={this.twitter}
                    align-icon-center="true"
                    text={this.twitterAccountName ? this.twitterAccountName : shortenLink(this.twitter)}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                {this.instagram && (
                  <eon-ui-link
                    target="_blank"
                    icon={LinkIcons.instagram}
                    href={this.instagram}
                    align-icon-center="true"
                    text={this.instagramAccountName ? this.instagramAccountName : shortenLink(this.instagram)}
                    icon-position={LINK_ICON_POSITION_LEFT}
                  ></eon-ui-link>
                )}
                <slot name="links"></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
